"use client";

import { useEffect, useRef, useState } from "react";

import { Tooltip, TooltipContent } from "@/components/ui/tooltip";

const TARGET = `:is(.chat-markdown, [data-streamdown="table-fullscreen"], [data-streamdown="mermaid-fullscreen"]) button:is([title], [data-tip])`;

function pick(node: EventTarget | null): HTMLElement | null {
  const el = node instanceof Element ? node.closest<HTMLElement>(TARGET) : null;

  if (!el) return null;

  const title = el.getAttribute("title");

  if (title) {
    el.dataset.tip = title;
    el.removeAttribute("title");

    if (!el.hasAttribute("aria-label")) el.setAttribute("aria-label", title);
  }

  return el;
}

export function StreamdownTooltips() {

  const [tip, setTip] = useState<{ anchor: HTMLElement | null; text: string; open: boolean }>({
    anchor: null,
    text: "",
    open: false,
  });
  const current = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const show = (event: Event) => {
      const el = pick(event.target);

      if (el && el !== current.current) {
        current.current = el;
        setTip({ anchor: el, text: el.dataset.tip ?? "", open: true });
      }
    };
    const hide = (event: Event) => {
      const next = (event as PointerEvent | FocusEvent).relatedTarget;

      if (current.current && !(next instanceof Node && current.current.contains(next))) {
        current.current = null;
        setTip((state) => ({ ...state, open: false }));
      }
    };

    document.addEventListener("pointerover", show);
    document.addEventListener("pointerout", hide);
    document.addEventListener("focusin", show);
    document.addEventListener("focusout", hide);

    return () => {
      document.removeEventListener("pointerover", show);
      document.removeEventListener("pointerout", hide);
      document.removeEventListener("focusin", show);
      document.removeEventListener("focusout", hide);
    };
  }, []);

  return (
    <Tooltip
      open={tip.open}
      onOpenChange={(open) => {
        if (!open) {
          current.current = null;
          setTip((state) => ({ ...state, open: false }));
        }
      }}
    >
      <TooltipContent anchor={tip.anchor}>{tip.text}</TooltipContent>
    </Tooltip>
  );
}
